import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateManualDividend } from "../../hooks/dividends";
import { useInvestments } from "../../hooks/investments";

export function ManualEntryDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const investments = useInvestments({ active: true });
  const create = useCreateManualDividend();
  const [investmentId, setInvestmentId] = useState("");
  const [exDate, setExDate] = useState("");
  const [payDate, setPayDate] = useState("");
  const [amountEur, setAmountEur] = useState("");
  const [currency, setCurrency] = useState("EUR");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = Number.parseFloat(amountEur);
    if (!Number.isFinite(parsed)) return;
    const cents = Math.round(parsed * 100);
    if (!investmentId || !exDate || !payDate || cents <= 0) return;
    await create.mutateAsync({
      investmentId,
      exDate,
      payDate,
      amountPerShareCents: cents,
      currency,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow-lg w-96 space-y-3">
        <h3 className="text-lg font-semibold">{t("dividends.manual_entry")}</h3>
        <label className="block">
          <span className="text-sm font-medium block mb-1">{t("dividends.form.investment")}</span>
          <select
            value={investmentId}
            onChange={(e) => setInvestmentId(e.target.value)}
            required
            className="w-full border rounded p-2"
          >
            <option value="">{t("dividends.form.select_investment")}</option>
            {(investments.data?.investments ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.symbol})
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium block mb-1">{t("dividends.form.ex_date")}</span>
          <input
            type="date"
            value={exDate}
            onChange={(e) => setExDate(e.target.value)}
            required
            className="w-full border rounded p-2"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium block mb-1">{t("dividends.form.pay_date")}</span>
          <input
            type="date"
            value={payDate}
            onChange={(e) => setPayDate(e.target.value)}
            required
            className="w-full border rounded p-2"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium block mb-1">
            {t("dividends.form.amount_per_share")}
          </span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amountEur}
            onChange={(e) => setAmountEur(e.target.value)}
            placeholder={t("dividends.form.amount_per_share")}
            required
            className="w-full border rounded p-2"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium block mb-1">{t("dividends.form.currency")}</span>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full border rounded p-2"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
            <option value="CHF">CHF</option>
            <option value="SEK">SEK</option>
            <option value="JPY">JPY</option>
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border rounded"
            disabled={create.isPending}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={create.isPending}
          >
            {create.isPending ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
